import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, Plus, Radio, Users, XCircle } from 'lucide-react';
import SubscriberAvatar from '../Common/SubscriberAvatar';
import {
  SUBSCRIBER_EMAIL_STATUSES,
  subscriberEmailStatus,
} from '../../lib/subscriberEmailStatus';
import type { AutomationStatus } from './types';
import type { AutomationEnrollmentRow, AutomationEnrollmentStatus } from './types';
import type { SubscriberRow } from '../Subscribers/types';

export interface EnrollmentWithSubscriber extends AutomationEnrollmentRow {
  subscriber: Pick<SubscriberRow, 'id' | 'email' | 'name' | 'email_status' | 'status' | 'custom_data'> | null;
}

function emailStatusLabel(row: EnrollmentWithSubscriber['subscriber']): string {
  const value = subscriberEmailStatus(row);
  if (!value) return '—';
  return SUBSCRIBER_EMAIL_STATUSES.find((s) => s.value === value)?.label ?? value;
}

function emailStatusClasses(value: string): string {
  const s = value.toLowerCase();
  if (s === 'confirmed') {
    return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800';
  }
  if (s === 'pending' || s === 'invited') {
    return 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800';
  }
  if (s === 'unsubscribed') {
    return 'bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200 border-rose-200 dark:border-rose-800';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
}

const ENROLLMENT_STATUS_STYLE: Record<AutomationEnrollmentStatus, string> = {
  active:
    'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800',
  completed:
    'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200 dark:border-blue-800',
  canceled:
    'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700',
};

function formatWhen(value: unknown): string {
  if (typeof value !== 'string') return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return value;
  }
}

const RELATIVE_UNITS: Array<{ limit: number; div: number; one: string; many: string }> = [
  { limit: 60, div: 1, one: 'sec', many: 'sec' },
  { limit: 60 * 60, div: 60, one: 'min', many: 'min' },
  { limit: 60 * 60 * 24, div: 60 * 60, one: 'hour', many: 'hours' },
  { limit: 60 * 60 * 24 * 30, div: 60 * 60 * 24, one: 'day', many: 'days' },
  { limit: 60 * 60 * 24 * 365, div: 60 * 60 * 24 * 30, one: 'month', many: 'months' },
  { limit: Infinity, div: 60 * 60 * 24 * 365, one: 'year', many: 'years' },
];

/** Past-tense relative time like "2 hours ago" / "3 days ago". */
function formatTimeAgo(targetIso: unknown, nowMs = Date.now()): string | null {
  if (typeof targetIso !== 'string' || !targetIso) return null;
  const targetMs = new Date(targetIso).getTime();
  if (Number.isNaN(targetMs)) return null;
  const diffSec = Math.round((nowMs - targetMs) / 1000);
  if (diffSec < 30) return 'just now';
  for (const u of RELATIVE_UNITS) {
    if (diffSec < u.limit) {
      const n = Math.max(1, Math.round(diffSec / u.div));
      return `${n} ${n === 1 ? u.one : u.many} ago`;
    }
  }
  return 'a while ago';
}

function EnrolledWhen({ value }: { value: unknown }) {
  const absolute = formatWhen(value);
  const ago = formatTimeAgo(value);
  return (
    <div>
      <div>{absolute}</div>
      {ago ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">{ago}</div>
      ) : null}
    </div>
  );
}

interface AutomationEnrollmentsTabProps {
  enrollments: EnrollmentWithSubscriber[];
  automationStatus: AutomationStatus;
  canEnroll: boolean;
  live?: boolean;
  onEnroll: () => void;
  onRequestCancel: (enrollment: EnrollmentWithSubscriber) => void;
}

export default function AutomationEnrollmentsTab({
  enrollments,
  automationStatus,
  canEnroll,
  live = false,
  onEnroll,
  onRequestCancel,
}: AutomationEnrollmentsTabProps) {
  const activeCount = enrollments.filter((e) => e.status === 'active').length;

  return (
    <section className="bg-white dark:bg-gray-800 min-w-0 overflow-hidden sm:rounded-xl sm:shadow sm:border sm:border-gray-200 sm:dark:border-gray-700">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <Users className="w-4 h-4 text-emerald-500 shrink-0" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
              Enrollments
            </h2>
            {enrollments.length > 0 ? (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {activeCount} active · {enrollments.length} total
              </span>
            ) : null}
            {live ? (
              <span
                title="Live updates connected"
                className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
              >
                <Radio className="w-3 h-3" />
                Live
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onEnroll}
            disabled={!canEnroll}
            title={
              canEnroll
                ? 'Enroll a subscriber in this automation'
                : `Set automation status to active (currently ${automationStatus})`
            }
            className="inline-flex items-center gap-1 px-2 py-1 sm:gap-1.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="sm:hidden">Enroll</span>
            <span className="hidden sm:inline">Enroll subscriber</span>
          </button>
        </div>
      </div>

      {enrollments.length === 0 ? (
        <div className="px-4 sm:px-6 py-10 text-sm text-gray-500 dark:text-gray-400 text-center space-y-3">
          <p>No subscribers enrolled yet.</p>
          {canEnroll ? (
            <button
              type="button"
              onClick={onEnroll}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4" />
              Enroll subscriber
            </button>
          ) : (
            <p className="text-xs">
              Set this automation to <span className="font-medium">active</span> to enroll subscribers.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto min-w-0">
          <table className="min-w-full w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Subscriber
                </th>
                <th
                  className="hidden md:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  title="Subscriber email_status (confirmed, pending, etc.)"
                >
                  Email status
                </th>
                <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Enrollment
                </th>
                <th className="px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Enrolled
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {enrollments.map((e) => {
                const subId =
                  e.subscriber?.id != null ? String(e.subscriber.id) : e.subscriber_id;
                const StatusIcon =
                  e.status === 'active'
                    ? Clock
                    : e.status === 'completed'
                      ? CheckCircle2
                      : XCircle;
                const emailStatus = subscriberEmailStatus(e.subscriber);
                return (
                  <tr key={e.id}>
                    <td className="px-4 py-2 sm:py-3 text-sm min-w-0">
                      <div className="flex items-start gap-3 min-w-0">
                        <SubscriberAvatar
                          customData={e.subscriber?.custom_data}
                          name={e.subscriber?.name}
                          email={e.subscriber?.email}
                        />
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/subscribers/${subId}`}
                            className="text-emerald-600 dark:text-emerald-400 hover:underline block truncate max-w-[10rem] sm:max-w-none"
                            title={
                              e.subscriber?.email || e.subscriber?.name || `subscriber ${subId}`
                            }
                          >
                            {e.subscriber?.email || e.subscriber?.name || `subscriber ${subId}`}
                          </Link>
                          {e.subscriber?.name && e.subscriber?.email ? (
                            <div className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 truncate">
                              {e.subscriber.name}
                            </div>
                          ) : null}
                          <div className="md:hidden mt-1">
                            <span
                              className={`inline-flex px-1.5 py-0.5 rounded-md text-[10px] font-medium border ${emailStatusClasses(emailStatus)}`}
                            >
                              {emailStatusLabel(e.subscriber)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-2 sm:px-4 py-2 sm:py-3 text-sm whitespace-nowrap">
                      <span
                        className={`inline-flex px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-medium border ${emailStatusClasses(emailStatus)}`}
                        title={emailStatus || 'No email status'}
                      >
                        {emailStatusLabel(e.subscriber)}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-sm whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-medium border ${ENROLLMENT_STATUS_STYLE[e.status]}`}
                        title={e.status}
                      >
                        <StatusIcon className="w-3 h-3 shrink-0" />
                        <span className="capitalize">{e.status}</span>
                      </span>
                    </td>
                    <td className="px-4 py-2 sm:py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                      <EnrolledWhen value={e.enrolled_at} />
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-right whitespace-nowrap">
                      {e.status === 'active' ? (
                        <button
                          type="button"
                          onClick={() => onRequestCancel(e)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Cancel
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
